import { events } from "@constants/tracking";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import { isNativeModulesDisabled } from "@memorang/helpers/native-runtime";
import { isWeb } from "@memorang/helpers/platform";
import AudioRecorder, {
	type AudioRecorderLabels,
	type AudioRecorderState,
	type AudioRecorderType,
} from "@memorang/ui/components/AudioRecorder";
import useRecorder from "@memorang/ui/components/AudioRecorderContainer/useRecorder";
import usePlayer from "@memorang/ui/hooks/usePlayer";
import { trackCustomEvent } from "analytics";
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from "react";
import { RecordingLimitAlert } from "./components/RecordingLimitAlert";

type Props = {
	recordedAudio: string | undefined;
	handleSetAudioUri: (uri: string | undefined) => void;
	disabled?: boolean;
	showDiscardButton?: boolean;
	maxDuration?: number;
	itemId?: number;
	type?: AudioRecorderType;
	labels?: AudioRecorderLabels;
	formatTimerLabel?: (seconds: number) => string;
};

export type AudioRecorderContainerRef = {
	stop: () => Promise<void>;
	isRecording: boolean;
};

type AudioCompressorModule = {
	Audio?: {
		compress: (
			uri: string,
			options?: {
				quality?: "low" | "medium" | "high";
			},
		) => Promise<string>;
	};
};

const compressAudioIfAvailable = async (uri: string): Promise<string> => {
	if (isWeb || isNativeModulesDisabled()) {
		return uri;
	}

	try {
		const module = require("react-native-compressor") as AudioCompressorModule;
		const compressedAudio = await module.Audio?.compress(uri, {
			quality: "medium",
		});
		return compressedAudio ?? uri;
	} catch (error) {
		console.warn(
			"[audio-recorder] Compressor unavailable, using original recording.",
			error,
		);
		return uri;
	}
};

const AudioRecorderContainer = forwardRef<AudioRecorderContainerRef, Props>(
	(
		{
			recordedAudio,
			handleSetAudioUri,
			disabled,
			showDiscardButton = true,
			maxDuration = 45,
			itemId,
			type = "default",
			labels,
			formatTimerLabel,
		},
		ref,
	) => {
		const [limitAlertVisible, setLimitAlertVisible] = useState(false);
		const [hasExpired, setHasExpired] = useState(false);

		const handleOnRecordingComplete = useCallback(
			async (uri: string) => {
				const finalUri = await compressAudioIfAvailable(uri);
				handleSetAudioUri(finalUri);
			},
			[handleSetAudioUri],
		);

		const {
			startRecording,
			stopRecording,
			clearMeterHistory,
			isError,
			isRecording,
			currentTime,
			meterHistory,
			duration,
			unloadRecording,
		} = useRecorder({
			onRecordingComplete: handleOnRecordingComplete,
			captureError: (_context, error) => {
				captureErrorToSentry(error, {
					error: serializeErrorForSentry(error),
				});
			},
		});

		useImperativeHandle(
			ref,
			() => ({
				stop: stopRecording,
				isRecording,
			}),
			[stopRecording, isRecording],
		);

		const handleTracking = useCallback(
			(status: "start" | "end") => {
				trackCustomEvent({
					eventName:
						status === "start"
							? events.sessionItemResponseAudioRecordingStarted
							: events.sessionItemResponseAudioRecordingEnded,
					itemId: itemId,
					status: status,
				});
			},
			[itemId],
		);

		const {
			playSound,
			pauseSound,
			isPlaying = false,
			currentTimeInMilliseconds = 0,
			totalTimeInMilliseconds = 0,
			onSeek,
			isEnded,
			unloadSound,
		} = usePlayer(recordedAudio ?? null, isWeb ? duration : undefined);

		const currentMode = isRecording || !recordedAudio ? "recording" : "playing";

		useEffect(() => {
			if (isRecording && currentTime > maxDuration * 1000) {
				stopRecording();
				setLimitAlertVisible(true);
				if (!recordedAudio) {
					setHasExpired(true);
				}
			}
		}, [isRecording, currentTime, maxDuration, stopRecording, recordedAudio]);

		useEffect(() => {
			if (recordedAudio) {
				setHasExpired(false);
			}
		}, [recordedAudio]);

		const handlePlayOrStop = useCallback(async () => {
			if (isEnded && recordedAudio) {
				await onSeek(0);
				playSound();
			} else if (isPlaying) {
				pauseSound();
			} else if (recordedAudio) {
				playSound();
			}
		}, [isEnded, recordedAudio, isPlaying, onSeek, playSound, pauseSound]);

		const handleRecord = useCallback(() => {
			// `disabled` gates capture only — playback stays available.
			if (recordedAudio) {
				handlePlayOrStop();
				return;
			}
			if (disabled) {
				return;
			}
			if (isRecording) {
				handleTracking("end");
				stopRecording();
			} else {
				handleTracking("start");
				setHasExpired(false);
				startRecording(0);
			}
		}, [
			disabled,
			recordedAudio,
			isRecording,
			handlePlayOrStop,
			handleTracking,
			stopRecording,
			startRecording,
		]);

		const handleDelete = useCallback(async () => {
			pauseSound();
			await new Promise((resolve) => setTimeout(resolve, 500)); // wait for audio to pause properly
			await unloadSound();
			unloadRecording();
			handleSetAudioUri(undefined);
			clearMeterHistory();
			setHasExpired(false);
		}, [
			pauseSound,
			unloadSound,
			unloadRecording,
			clearMeterHistory,
			handleSetAudioUri,
		]);

		const state: AudioRecorderState = useMemo(() => {
			if (isRecording) {
				return "recording";
			}
			if (recordedAudio) {
				return "recorded";
			}
			if (hasExpired) {
				return "expired";
			}
			return "default";
		}, [isRecording, recordedAudio, hasExpired]);

		const isLoading =
			!recordedAudio && meterHistory.length > 0 && !isError && !isRecording;

		const progress =
			totalTimeInMilliseconds > 0
				? currentTimeInMilliseconds / totalTimeInMilliseconds
				: 0;

		const handleSeek = useCallback(
			async (newProgress: number) => {
				await onSeek(newProgress * totalTimeInMilliseconds);
			},
			[totalTimeInMilliseconds, onSeek],
		);

		const totalTime =
			currentMode === "recording"
				? maxDuration * 1000
				: totalTimeInMilliseconds;
		const currentTimeForDisplay =
			currentMode === "recording" ? currentTime : currentTimeInMilliseconds;
		const remainingSeconds = Math.max(
			0,
			Math.ceil((maxDuration * 1000 - currentTime) / 1000),
		);

		return (
			<>
				<AudioRecorder
					state={state}
					type={type}
					disabled={disabled}
					showDiscardButton={showDiscardButton}
					handleDelete={handleDelete}
					handleRecord={handleRecord}
					isLoading={isLoading}
					isPlaying={isPlaying}
					meterHistory={meterHistory}
					progress={progress}
					onSeek={handleSeek}
					totalTime={totalTime}
					currentTime={currentTimeForDisplay}
					remainingSeconds={remainingSeconds}
					labels={labels}
					formatTimerLabel={formatTimerLabel}
				/>
				<RecordingLimitAlert
					maxDurationSeconds={maxDuration}
					visible={limitAlertVisible}
					onDismiss={() => setLimitAlertVisible(false)}
				/>
			</>
		);
	},
);

export default AudioRecorderContainer;
